import React from 'react';
import { InlineMath, BlockMath } from 'react-katex';
import 'katex/dist/katex.min.css';

// Shared helper component to parse and render mixed text and math.
// Strategy: scan character by character. When a $ is found, look ahead for a
// closing $. Only treat the pair as LaTeX math if the content between them
// contains actual LaTeX indicators (backslash, ^, _, { or }).
// Plain currency like $45 or $105.75 has none of these, so it stays as plain text.
const MathParser = ({ text }) => {
  if (!text) return null;

  const result = [];
  let i = 0;
  let textBuffer = '';
  let keyCounter = 0;

  const flushText = () => {
    if (textBuffer) {
      result.push(<span key={`text-${keyCounter++}`}>{textBuffer}</span>);
      textBuffer = '';
    }
  };

  while (i < text.length) {
    // Not a dollar sign — accumulate as plain text
    if (text[i] !== '$') {
      textBuffer += text[i++];
      continue;
    }

    // Check for block math: $$...$$
    if (i + 1 < text.length && text[i + 1] === '$') {
      const start = i + 2;
      const closeIdx = text.indexOf('$$', start);
      if (closeIdx !== -1) {
        flushText();
        const content = text.substring(start, closeIdx);
        try {
          result.push(<BlockMath key={`math-${keyCounter++}`}>{content}</BlockMath>);
        } catch (e) {
          console.error('KaTeX block error:', content, e);
          result.push(<span key={`err-${keyCounter++}`} style={{ color: 'red' }}>{text.substring(i, closeIdx + 2)}</span>);
        }
        i = closeIdx + 2;
        continue;
      }
    }

    // Look for a closing $ to form an inline math pair
    let j = i + 1;
    while (j < text.length && text[j] !== '$') {
      if (text[j] === '\\') j++; // skip over escaped character
      j++;
    }

    if (j < text.length) {
      // Potential inline math pair found: text[i..j]
      const content = text.substring(i + 1, j);
      // Check if it explicitly contains LaTeX commands
      const hasLatex = /[\\^_{}]/.test(content);
      
      let isMath = hasLatex;
      if (!hasLatex) {
        // If there are no explicit LaTeX characters, we apply strict heuristics 
        // to prevent normal currency/text from being swallowed.
        // Rule 1: Inline math shouldn't start or end with whitespace.
        const validSpacing = !/^\s/.test(content) && !/\s$/.test(content);
        // Rule 2: It shouldn't contain long English words (3+ letters), which 
        // implies plain text accidentally caught between two currency symbols.
        const noLongWords = !/[a-zA-Z]{3,}/.test(content);
        
        if (validSpacing && noLongWords && content.length > 0) {
          isMath = true;
        }
      }

      if (isMath) {
        flushText();
        try {
          result.push(<InlineMath key={`math-${keyCounter++}`}>{content}</InlineMath>);
        } catch (e) {
          console.error('KaTeX inline error:', content, e);
          result.push(<span key={`err-${keyCounter++}`} style={{ color: 'red' }}>{`$${content}$`}</span>);
        }
        i = j + 1;
        continue;
      }
    }

    // $ is not part of a valid math pair — treat it as a plain text character
    textBuffer += text[i++];
  }

  flushText();

  return <>{result}</>;
};

export default MathParser;
